/**
 * Dataset loading and sampling for LVC simulations.
 *
 * Two problems this solves. Datasets used to be fetched from inside the 100 ms tick, so a
 * dataset that had not arrived yet was requested again on every tick, and each late response
 * overwrote agent positions with an older copy (agents jumped backwards). And every tick
 * scanned the whole location array to find the nearest sample.
 *
 * Here a dataset is fetched once, keyed by id, with concurrent callers sharing one promise.
 * Each behavior gets a sorted timestamp index, and sampling walks a cursor forward, which is
 * O(1) for the monotonic time a running simulation asks for. Between samples the position is
 * interpolated, so agents glide at frame rate instead of stepping once a second.
 *
 * Plan: plans/lvc-revamp/eng-plan.md.
 */

import { fetchDatasetLocations } from './datasetsService';

const inFlight = new Map(); // dataset_id -> Promise<locations>
const loaded = new Map(); // dataset_id -> locations

/**
 * Fetch one dataset's locations at most once. Concurrent callers share the same promise, so
 * a tick that fires mid-download does not start a second one.
 * @param {string} datasetId
 * @param {Function} [addError]
 * @returns {Promise<Array>}
 */
export const loadDataset = (datasetId, addError) => {
  if (loaded.has(datasetId)) return Promise.resolve(loaded.get(datasetId));
  if (inFlight.has(datasetId)) return inFlight.get(datasetId);

  const promise = fetchDatasetLocations(datasetId, addError)
    .then((locations) => {
      const result = locations || [];
      loaded.set(datasetId, result);
      inFlight.delete(datasetId);
      return result;
    })
    .catch((error) => {
      inFlight.delete(datasetId);
      throw error;
    });

  inFlight.set(datasetId, promise);
  return promise;
};

/**
 * Sort a dataset's locations into the behavior's window and build the lookup index.
 * @param {Array} locations - raw dataset locations
 * @param {number} startTime - behavior start_time
 * @param {number} endTime - behavior end_time
 * @returns {{times: Float64Array, points: Array}} empty index when nothing is in range
 */
export const indexLocations = (locations, startTime, endTime) => {
  const points = (locations || [])
    .filter((loc) => loc && loc.timestamp >= startTime && loc.timestamp <= endTime)
    .sort((a, b) => a.timestamp - b.timestamp);
  const times = new Float64Array(points.length);
  points.forEach((point, i) => { times[i] = point.timestamp; });
  return { times, points };
};

const lerp = (a, b, f) => a + (b - a) * f;

const lerpMaybe = (a, b, f) => {
  if (typeof a !== 'number') return typeof b === 'number' ? b : a;
  if (typeof b !== 'number') return a;
  return lerp(a, b, f);
};

/** Headings wrap, so interpolate the short way around the circle. */
const lerpAngle = (a, b, f) => {
  if (typeof a !== 'number') return typeof b === 'number' ? b : a;
  if (typeof b !== 'number') return a;
  const delta = ((((b - a) % 360) + 540) % 360) - 180;
  return (((a + delta * f) % 360) + 360) % 360;
};

const nearExact = (point, t) => (Math.abs(point.timestamp - t) < 0.5 ? point : null);

const toPosition = (point) => (point ? { lat: point.lat, lon: point.lon } : null);

/** Where `t` sits in the index. Starts from `cursor`, so replaying forward costs O(1). */
const seek = (times, t, cursor) => {
  let i = Math.min(Math.max(cursor, 0), Math.max(times.length - 1, 0));
  if (times[i] > t) {
    // Went backwards (a scrub); fall back to a binary search.
    let lo = 0;
    let hi = times.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (times[mid] <= t) lo = mid + 1;
      else hi = mid;
    }
    i = Math.max(lo - 1, 0);
  } else {
    while (i + 1 < times.length && times[i + 1] <= t) i += 1;
  }
  return i;
};

/**
 * The reading at simulation-relative timestamp `t`.
 *
 * @param {{times: Float64Array, points: Array}} index
 * @param {number} t - timestamp in the dataset's own time base
 * @param {number} [cursor] - index returned by the previous call for this behavior
 * @returns {{sample: Object|null, exact: Object|null, cursor: number, position: Object|null}}
 *   `sample` is the interpolated reading used for display. `exact` is the real sample at `t`
 *   (within half a second) or null, which is what the upload sends so nothing invented
 *   reaches Firestore.
 */
export const sampleAt = (index, t, cursor = 0) => {
  const { times, points } = index;
  if (!points || points.length === 0) return { sample: null, exact: null, cursor: 0, position: null };

  const i = seek(times, t, cursor);
  const current = points[i];

  if (t <= times[0]) return { sample: points[0], exact: nearExact(points[0], t), cursor: 0, position: toPosition(points[0]) };
  if (i >= points.length - 1) {
    const last = points[points.length - 1];
    return { sample: last, exact: nearExact(last, t), cursor: points.length - 1, position: toPosition(last) };
  }

  const next = points[i + 1];
  const span = times[i + 1] - times[i];
  const factor = span > 0 ? (t - times[i]) / span : 0;
  const blended = {
    ...current,
    lat: lerp(current.lat, next.lat, factor),
    lon: lerp(current.lon, next.lon, factor),
    altitude: lerpMaybe(current.altitude, next.altitude, factor),
    speed: lerpMaybe(current.speed, next.speed, factor),
    course: lerpAngle(current.course, next.course, factor),
  };

  const exact = nearExact(current, t) || nearExact(next, t);
  return { sample: blended, exact, cursor: i, position: toPosition(blended) };
};

/** Key for one agent's behavior in the preloaded map. */
export const behaviorKey = (uid, behaviorIndex) => `${uid}:${behaviorIndex}`;

/**
 * Load and index every `datasetview` behavior across the agents, before the run starts.
 * Returns a map so the clock can look an agent's behavior up without touching the network.
 *
 * @param {Array} agents - scenario virtual agents, each with `uid` and `behaviors`
 * @param {Function} [addError]
 * @returns {Promise<Map<string, {times: Float64Array, points: Array}>>} keyed `${uid}:${behaviorIndex}`
 */
export const preloadBehaviors = async (agents, addError) => {
  const jobs = [];
  (agents || []).forEach((agent) => {
    (agent.behaviors || []).forEach((behavior, behaviorIndex) => {
      if (behavior.type !== 'datasetview' || !behavior.dataset_id) return;
      jobs.push(
        loadDataset(behavior.dataset_id, addError)
          .then((locations) => [
            behaviorKey(agent.uid, behaviorIndex),
            indexLocations(locations, behavior.start_time, behavior.end_time),
          ])
          .catch((error) => {
            console.error(`[lvc] dataset ${behavior.dataset_id} failed to load:`, error);
            return [behaviorKey(agent.uid, behaviorIndex), indexLocations([], 0, 0)];
          })
      );
    });
  });
  return new Map(await Promise.all(jobs));
};

/** Drop everything cached. Called when a scenario page unmounts. */
export const clearDatasetCache = () => {
  inFlight.clear();
  loaded.clear();
};
